import React from "react";
import { useWizard } from "@/context/WizardContext";
import { Grid, TextInput, SelectInput, TextArea, SectionHeading } from "@/components/wizard/FormKit";
import SavedProfilePicker from "@/components/wizard/SavedProfilePicker";
import PhotoExtractButton from "@/components/wizard/PhotoExtractButton";
import AvidaSeedBanner from "@/components/wizard/AvidaSeedBanner";
import { Pencil, Lock as LockIcon, CheckCircle2 } from "lucide-react";
import {
  listAgencies, listPhysicians, saveAgency, savePhysician,
  deleteAgency, deletePhysician,
} from "@/lib/savedProfiles";
import { useAssignmentContext } from "@/lib/integrationMode";

function isStandaloneMode() {
  try { return window.sessionStorage.getItem("wi-pa-standalone-mode-v1") === "1"; }
  catch { return false; }
}

/**
 * AgencyReadonly — clean dl-style display of locked agency fields.
 *
 * Rendered instead of the 7 disabled form inputs when the agency is
 * managed upstream (Avida / parent-app / preview). Far less visual
 * noise than a grid of disabled inputs and makes it obvious the RN
 * is not expected to edit these values.
 */
function AgencyReadonly({ agency }) {
  const rows = [
    { label: "Agency Name", value: agency.name, testid: "agency-ro-name" },
    { label: "Provider Number", value: agency.provider_number, testid: "agency-ro-provider-number" },
    { label: "Agency NPI", value: agency.npi, testid: "agency-ro-npi" },
    { label: "Contact Person", value: agency.contact_name, testid: "agency-ro-contact" },
    { label: "Phone", value: agency.phone, testid: "agency-ro-phone" },
    { label: "Fax", value: agency.fax, testid: "agency-ro-fax" },
    { label: "Address", value: agency.address, testid: "agency-ro-address", span: 2 },
  ];
  return (
    <dl
      className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 rounded-xl bg-[#F7FAFC] border border-[#E7EEF3] px-5 py-4"
      data-testid="agency-readonly"
    >
      {rows.map((row) => (
        <div
          key={row.testid}
          className={`py-1.5 ${row.span === 2 ? "sm:col-span-2" : ""}`}
        >
          <dt className="text-[11px] uppercase tracking-[0.12em] text-[#3A5275] font-semibold mb-0.5">
            {row.label}
          </dt>
          <dd
            className="text-[14px] text-[#0B1E3A] font-medium"
            data-testid={row.testid}
          >
            {row.value && String(row.value).trim()
              ? row.value
              : <span className="text-[#94A3B8] font-normal">—</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function Step1Agency() {
  const {
    data, update, isPrefilled, avidaDraftId,
    recertMode, recertSourceLabel, recertOverride, setRecertOverride,
  } = useWizard();
  const standaloneMode = isStandaloneMode();
  const [agencies, setAgencies] = React.useState(standaloneMode ? listAgencies() : []);
  const [physicians, setPhysicians] = React.useState(listPhysicians());
  const assignmentCtx = useAssignmentContext();

  // Pre-fill tint (Prompt 6B). Spread `{...pf("client","first_name")}`
  // onto any input that carries a field the Avida coordinator may have
  // seeded. When the RN edits the field, `update()` drops the key from
  // prefilledFields — the tint + hint disappear on the next render.
  const pf = (section, key) => ({ prefilled: isPrefilled(section, key) });

  // Agency lock matrix:
  //   • avida-seeded → locked (managed by Avida coordinator)
  //   • parent-app deep-link → locked (managed by upstream agency)
  //   • preview-mode + OrganizationPicker → locked (selected once on landing)
  //   • STANDALONE mode → UNLOCKED. The RN is in the field with a patient
  //     not yet seeded; she must enter the agency manually. The saved-
  //     agencies picker reappears so a one-tap can populate from her last
  //     visit.
  const agencyLocked = !standaloneMode;
  const agencyLockedCopy = (
    avidaDraftId
      ? "Managed by Avida Care — these details are locked for this intake."
      : assignmentCtx.source === "parent"
        ? "Managed by your agency assignment — these details are locked for this intake."
        : assignmentCtx.source === "preview"
          ? "Preview mode — agency selected via the preview org picker. To change, return to the landing page."
          : "Managed upstream — these details are locked for this intake."
  );
  const agencyLockPillText = (
    avidaDraftId
      ? "Locked · managed by Avida Care"
      : assignmentCtx.source === "parent"
        ? "Locked · managed by your agency"
        : assignmentCtx.source === "preview"
          ? "Locked · preview-selected organization"
          : "Locked · managed upstream"
  );
  // Per-source hint shown inside locked agency fields (FormKit's
  // default hint says "Managed by Avida Care", which is wrong for
  // parent-app and preview flows).
  const agencyLockedFieldHint = (
    avidaDraftId
      ? "Managed by Avida Care — contact the coordinator to update"
      : assignmentCtx.source === "parent"
        ? "Managed by your agency — contact the assignor to update"
        : assignmentCtx.source === "preview"
          ? "Preview mode — return to the landing page to change the org"
          : "Managed upstream — contact the assignor to update"
  );
  // Physician + insurance still come from Avida Care only — keep the
  // legacy lock semantics (only when avida-sourced).
  const physicianLocked = !!avidaDraftId;
  const insuranceLocked = !!avidaDraftId;
  const ag = agencyLocked ? { locked: true, lockedHint: agencyLockedFieldHint } : {};
  const ph = physicianLocked ? { locked: true } : {};
  const ins = insuranceLocked ? { locked: true } : {};

  // Recert "verified from last visit" mode applies to Section B
  // demographics. Only fields with a non-empty value lock — empty
  // fields stay editable (e.g. Assessment Date is always scrubbed by
  // the snapshot endpoint and must be filled for the current visit).
  // The RN can flip `recertOverride` on to unlock the entire block.
  const demoVerified = (key) => {
    if (!recertMode || recertOverride) return {};
    const v = data.client?.[key];
    if (v == null || v === "") return {};
    return { verified: true, verifiedLabel: recertSourceLabel || "last visit" };
  };
  const ageInsuranceVerified = (key) => {
    // The 4 insurance-flavoured client.* keys (insurance_type / mco_*
    // / forwardhealth_member_id) are also subject to recert lock —
    // BUT only when not already locked by the Avida flow above.
    if (insuranceLocked) return {};
    return demoVerified(key);
  };

  const avidaBanner = avidaDraftId ? {
    draftId: avidaDraftId,
    clientName: `${data.client.first_name || ""} ${data.client.last_name || ""}`.trim(),
  } : null;

  // Standalone mode helpers — the RN can save & re-apply her last-used
  // agencies/physicians. Re-enabled here (was removed for the always-
  // locked agency boundary) because in standalone mode there's no
  // upstream pin so we let her pick from her own history.
  const applyAgency = (p) => update("agency", {
    name: p.name || "", provider_number: p.provider_number || "", npi: p.npi || "",
    address: p.address || "", phone: p.phone || "", fax: p.fax || "",
    contact_name: p.contact_name || "",
  });
  const saveCurrentAgency = () => {
    const res = saveAgency(data.agency);
    if (res.ok) setAgencies(listAgencies());
    return res;
  };
  const removeAgency = (id) => { deleteAgency(id); setAgencies(listAgencies()); };

  const applyPhysician = (p) => update("physician", {
    name: p.name || "", npi: p.npi || "", address: p.address || "",
    phone: p.phone || "", fax: p.fax || "",
  });
  const saveCurrentPhysician = () => {
    const res = savePhysician(data.physician);
    if (res.ok) setPhysicians(listPhysicians());
    return res;
  };
  const removePhysician = (id) => { deletePhysician(id); setPhysicians(listPhysicians()); };

  const applyInsuranceCard = (payload) => {
    const patch = {};
    if (payload.member_id)     patch.medicaid_number = payload.member_id;
    if (payload.group_number)  patch.other_policy    = payload.group_number;
    if (payload.plan_name)     patch.other_name      = payload.plan_name;
    if (Object.keys(patch).length) update("insurance", patch);
  };
  return (
    <div className="space-y-10">
      <section className="card-surface fade-up" data-testid="step1-section-agency">
        <SectionHeading
          eyebrow={standaloneMode ? "Step 1 · Section A · Standalone" : "Step 1 · Section A"}
          title="Agency Information"
          description={standaloneMode
            ? "Standalone intake — fill the agency manually (or pick from your last visit). A coordinator will reconcile this to a parent-app pipeline later."
            : agencyLockedCopy}
        />
        {standaloneMode ? (
          <div className="mb-4">
            <SavedProfilePicker
              label="Saved agencies"
              testid="agency-profiles"
              profiles={agencies}
              onApply={applyAgency}
              onSave={saveCurrentAgency}
              onDelete={removeAgency}
              emptyHint="Fill Agency Name / Provider # below, then Save current — it'll auto-fill on the next standalone visit."
            />
          </div>
        ) : (
          <div
            className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#B7CADA] bg-[#EEF5FC] px-2.5 py-1 text-[11px] font-semibold text-[#0A3D79]"
            data-testid="agency-locked-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1565B8]" />
            {agencyLockPillText}
          </div>
        )}
        <Grid cols={2}>
          {standaloneMode ? (
            <>
              <TextInput label="Agency Name" required testid="agency-name" {...ag}
                value={data.agency.name} onChange={(v) => update("agency", { name: v })} />
              <TextInput label="Provider Number" required testid="agency-provider-number" {...ag}
                value={data.agency.provider_number} onChange={(v) => update("agency", { provider_number: v })} />
              <TextInput label="Agency NPI" testid="agency-npi" {...ag}
                value={data.agency.npi} onChange={(v) => update("agency", { npi: v })} />
              <TextInput label="Contact Person" testid="agency-contact" {...ag}
                value={data.agency.contact_name} onChange={(v) => update("agency", { contact_name: v })} />
              <TextInput label="Phone" required testid="agency-phone" {...ag}
                value={data.agency.phone} onChange={(v) => update("agency", { phone: v })} />
              <TextInput label="Fax" testid="agency-fax" {...ag}
                value={data.agency.fax} onChange={(v) => update("agency", { fax: v })} />
            </>
          ) : (
            <AgencyReadonly agency={data.agency} />
          )}
        </Grid>
        {standaloneMode ? (
          <div className="mt-5">
            <TextArea label="Agency Address" testid="agency-address" {...ag}
              value={data.agency.address} onChange={(v) => update("agency", { address: v })} />
          </div>
        ) : null}
      </section>

      <AvidaSeedBanner banner={avidaBanner} />

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 1 · Section B" title="Client Demographics"
          description="Identifying information for the member receiving personal care." />
        {recertMode && (
          <div className="mb-5 flex items-center gap-3 flex-wrap">
            {!recertOverride ? (
              <>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#B5D9C3] bg-[#E9F5EE] px-2.5 py-1 text-[11px] font-semibold text-[#0E6B3F]"
                  data-testid="recert-verified-pill"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Recert · verified from {recertSourceLabel || "last visit"}
                </span>
                <button
                  type="button"
                  onClick={() => setRecertOverride(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#D6E3EF] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#0A3D79] hover:bg-[#F1F5F9]"
                  data-testid="recert-override-on"
                >
                  <Pencil className="w-3 h-3" />
                  Update demographics
                </button>
              </>
            ) : (
              <>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#F0E29A] bg-[#FFFDEC] px-2.5 py-1 text-[11px] font-semibold text-[#8A6B00]"
                  data-testid="recert-editing-pill"
                >
                  <Pencil className="w-3 h-3" />
                  Editing demographics
                </span>
                <button
                  type="button"
                  onClick={() => setRecertOverride(false)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#D6E3EF] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#0A3D79] hover:bg-[#F1F5F9]"
                  data-testid="recert-override-off"
                >
                  <LockIcon className="w-3 h-3" />
                  Lock again
                </button>
              </>
            )}
          </div>
        )}
        <Grid cols={3}>
          <TextInput label="First Name" required testid="client-first" {...pf("client","first_name")} {...demoVerified("first_name")}
            value={data.client.first_name} onChange={(v) => update("client", { first_name: v })} />
          <TextInput label="Middle Initial" testid="client-middle" {...pf("client","middle_initial")} {...demoVerified("middle_initial")}
            value={data.client.middle_initial} onChange={(v) => update("client", { middle_initial: v })} />
          <TextInput label="Last Name" required testid="client-last" {...pf("client","last_name")} {...demoVerified("last_name")}
            value={data.client.last_name} onChange={(v) => update("client", { last_name: v })} />
          <TextInput label="Date of Birth" type="date" required testid="client-dob" {...pf("client","date_of_birth")} {...demoVerified("date_of_birth")}
            value={data.client.date_of_birth} onChange={(v) => update("client", { date_of_birth: v })} />
          <SelectInput label="Gender" testid="client-sex" {...pf("client","gender")} {...demoVerified("gender")}
            value={data.client.gender} onChange={(v) => update("client", { gender: v })}
            options={["Female", "Male", "Non-binary", "Prefer not to say"]} />
          <TextInput label="Race / Ethnicity" testid="client-race" {...demoVerified("race")}
            value={data.client.race} onChange={(v) => update("client", { race: v })} />
          <TextInput label="Preferred Language" testid="client-language" {...pf("client","language_preferred")} {...demoVerified("language_preferred")}
            value={data.client.language_preferred} onChange={(v) => update("client", { language_preferred: v })} />
          <SelectInput label="Marital Status" testid="client-marital" {...demoVerified("marital_status")}
            value={data.client.marital_status} onChange={(v) => update("client", { marital_status: v })}
            options={["Single", "Married", "Widowed", "Divorced", "Separated"]} />
          <TextInput label="SSN (last 4)" testid="client-ssn-last4" maxLength={4} {...pf("client","ssn_last4")} {...demoVerified("ssn_last4")}
            value={data.client.ssn_last4} onChange={(v) => update("client", { ssn_last4: v.replace(/\D/g, "").slice(0, 4) })} />
        </Grid>
        <Grid cols={2} className="mt-5">
          <TextInput label="County of Residence" testid="client-county" {...pf("client","county_of_residence")} {...demoVerified("county_of_residence")}
            value={data.client.county_of_residence} onChange={(v) => update("client", { county_of_residence: v })} />
          <TextInput label="County of Responsibility" testid="client-county-responsibility" {...pf("client","county_of_responsibility")} {...demoVerified("county_of_responsibility")}
            value={data.client.county_of_responsibility} onChange={(v) => update("client", { county_of_responsibility: v })} />
        </Grid>
        <Grid cols={4} className="mt-5">
          <div className="md:col-span-2">
            <TextInput label="Street Address" testid="client-address-street" {...pf("client","address_street")} {...demoVerified("address_street")}
              value={data.client.address_street} onChange={(v) => update("client", { address_street: v })} />
          </div>
          <TextInput label="City" testid="client-address-city" {...pf("client","address_city")} {...demoVerified("address_city")}
            value={data.client.address_city} onChange={(v) => update("client", { address_city: v })} />
          <Grid cols={2}>
            <TextInput label="State" testid="client-address-state" maxLength={2} {...pf("client","address_state")} {...demoVerified("address_state")}
              value={data.client.address_state} onChange={(v) => update("client", { address_state: v.toUpperCase().slice(0, 2) })} />
            <TextInput label="ZIP" testid="client-address-zip" maxLength={10} {...pf("client","address_zip")} {...demoVerified("address_zip")}
              value={data.client.address_zip} onChange={(v) => update("client", { address_zip: v })} />
          </Grid>
        </Grid>
        <Grid cols={3} className="mt-5">
          <TextInput label="Primary Phone" testid="client-phone" {...pf("client","phone_primary")} {...demoVerified("phone_primary")}
            value={data.client.phone_primary} onChange={(v) => update("client", { phone_primary: v })} />
          <TextInput label="Emergency Contact" testid="client-emergency" {...demoVerified("emergency_contact")}
            value={data.client.emergency_contact} onChange={(v) => update("client", { emergency_contact: v })} />
          <TextInput label="Emergency Phone" testid="client-emergency-phone" {...demoVerified("emergency_phone")}
            value={data.client.emergency_phone} onChange={(v) => update("client", { emergency_phone: v })} />
          <TextInput label="ForwardHealth Member ID" required testid="client-member-id" {...pf("client","forwardhealth_member_id")} {...ageInsuranceVerified("forwardhealth_member_id")}
            value={data.client.forwardhealth_member_id} onChange={(v) => update("client", { forwardhealth_member_id: v })} />
          <TextInput label="Medical Record #" testid="client-mrn" {...demoVerified("medical_record_number")}
            value={data.client.medical_record_number} onChange={(v) => update("client", { medical_record_number: v })} />
          <TextInput label="Assessment Date" type="date" testid="client-assessment-date"
            value={data.client.assessment_date} onChange={(v) => update("client", { assessment_date: v })} />
        </Grid>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 1 · Section C" title="Insurance & Physician"
          description="ForwardHealth coverage and primary care physician (signer of CMS-485)." />
        {!insuranceLocked && (
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <PhotoExtractButton
              testid="photo-insurance"
              kind="insurance_card"
              title="Photo of insurance card"
              blurb="Tap to auto-fill Member ID, Group #, and plan name from a tablet camera photo."
              onExtract={applyInsuranceCard}
            />
          </div>
        )}
        {insuranceLocked && (
          <div
            className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-[#B7CADA] bg-[#EEF5FC] px-2.5 py-1 text-[11px] font-semibold text-[#0A3D79]"
            data-testid="insurance-locked-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1565B8]" />
            Locked · insurance supplied by Avida Care
          </div>
        )}
        <Grid cols={2}>
          <SelectInput label="Insurance Type" testid="ins-type" {...ins}
            value={data.client.insurance_type} onChange={(v) => update("client", { insurance_type: v })}
            options={["", "Medicaid", "MCO", "Private"]} />
          <TextInput label="MCO Name" testid="ins-mco-name" {...ins}
            value={data.client.mco_name} onChange={(v) => update("client", { mco_name: v })} />
          <TextInput label="MCO Member ID" testid="ins-mco-member-id" {...ins}
            value={data.client.mco_member_id} onChange={(v) => update("client", { mco_member_id: v })} />
          <TextInput label="Medicaid Number" testid="ins-medicaid" {...ins}
            value={data.insurance.medicaid_number} onChange={(v) => update("insurance", { medicaid_number: v })} />
          <TextInput label="Other Insurance" testid="ins-other" {...ins}
            value={data.insurance.other_name} onChange={(v) => update("insurance", { other_name: v })} />
        </Grid>
        <div className="mt-6 h-px bg-[#D6E3EF]" />
        {!physicianLocked && (
          <div className="mt-6 mb-4">
            <SavedProfilePicker
              label="Saved physicians"
              testid="physician-profiles"
              profiles={physicians}
              onApply={applyPhysician}
              onSave={saveCurrentPhysician}
              onDelete={removePhysician}
              emptyHint="Fill Physician Name + NPI below, then Save current. Next client: one tap to recall."
            />
          </div>
        )}
        {physicianLocked && (
          <div
            className="mt-6 mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#B7CADA] bg-[#EEF5FC] px-2.5 py-1 text-[11px] font-semibold text-[#0A3D79]"
            data-testid="physician-locked-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1565B8]" />
            Locked · physician supplied by Avida Care
          </div>
        )}
        <Grid cols={2}>
          <TextInput label="Physician Name" required testid="phys-name" {...ph}
            value={data.physician.name} onChange={(v) => update("physician", { name: v })} />
          <TextInput label="Physician NPI" required testid="phys-npi" {...ph}
            value={data.physician.npi} onChange={(v) => update("physician", { npi: v })} />
          <TextInput label="Clinic Address" testid="phys-address" {...ph}
            value={data.physician.address} onChange={(v) => update("physician", { address: v })} />
          <TextInput label="Phone" testid="phys-phone" {...ph}
            value={data.physician.phone} onChange={(v) => update("physician", { phone: v })} />
          <TextInput label="Fax (for CMS-485 signature)" testid="phys-fax" {...ph}
            value={data.physician.fax} onChange={(v) => update("physician", { fax: v })} />
        </Grid>
      </section>
    </div>
  );
}
